import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { fetchBookingRules } from '../../api/lib/booking-rules';
import { bookingRulesKeys } from '../../api/query-keys/booking-rules';
import { BookingRulesActionsTableMenu } from '../molecules/booking-rules-actions-table-menu';
import { BookingRulesFilterChips } from '../molecules/filters/booking-rules-filter-chips';
import { BookingRulesTypeFilterPopover } from '../molecules/filters/booking-rules-type-filter-popover';
import { BookingRulesMoreFilters } from '../organisms/booking-rules-more-filters';
import { BookingRulesTable } from '../organisms/booking-rules-table';
import { BookingRulesActionsSlotPayload } from '../organisms/booking-rules-table-row';
import { DeleteBookingRulesDialog } from '../organisms/dialogs/delete-booking-rules-dialog';
import { EditBookingRulesNameDialog } from '../organisms/dialogs/edit-booking-rules-name-dialog';

const BookingRules = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/booking-rules/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    fetchBookingRules,
    bookingRulesKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const [selectedBookingRules, setSelectedBookingRules] =
    useState<BookingRulesActionsSlotPayload | null>(null);

  const [
    isEditBookingRulesNameDialogOpen,
    handleOpenEditBookingRulesNameDialog,
    handleCloseEditBookingRulesNameDialog,
  ] = useDialog();

  const [
    isDeleteBookingRulesDialogOpen,
    handleOpenDeleteBookingRulesDialog,
    handleCloseDeleteBookingRulesDialog,
  ] = useDialog();

  const actionsSlot = useCallback(
    ({
      token,
      name,
      employeesGroupsTokens,
    }: BookingRulesActionsSlotPayload) => (
      <BookingRulesActionsTableMenu
        onEditName={() => {
          setSelectedBookingRules({ token, name, employeesGroupsTokens });
          handleOpenEditBookingRulesNameDialog();
        }}
        onDelete={() => {
          setSelectedBookingRules({ token, name, employeesGroupsTokens });
          handleOpenDeleteBookingRulesDialog();
        }}
      />
    ),
    [],
  );

  useEffect(() => {
    if (selectedBookingRules) {
      const updatedBookingRules = results?.hits.find(
        bookingRule => bookingRule.token === selectedBookingRules.token,
      );

      if (updatedBookingRules) {
        setSelectedBookingRules({
          token: updatedBookingRules.token,
          name: updatedBookingRules.name,
          employeesGroupsTokens: updatedBookingRules.assignedEmployeeGroups.map(
            group => group.token,
          ),
        });
      }
    }
  }, [results?.hits]);

  return (
    <>
      <Page>
        <PageHeader
          title={t('bookingRules:common.title')}
          extras={
            <Can I="create" a="BookingRules" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={() =>
                        navigate({
                          to: '/tenant/booking-rules/create',
                          state: { backUrl: location.href },
                        })
                      }
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="contained"
                      id="create_booking_rules_button"
                      size="large">
                      {t('bookingRules:common.createNewRules')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          }
        />

        <Page.ContentContainer>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 3,
              py: 2.5,
            }}>
            <Query
              searchAsType
              disabled={false}
              onChange={handleChangeQuery}
              sx={{
                minWidth: 200,
              }}
              value={query}
            />

            <BookingRulesTypeFilterPopover
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
            />

            <BookingRulesMoreFilters
              anchor={'right'}
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
              resetFilters={resetFilters}
            />
          </Box>

          <BookingRulesFilterChips
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <BookingRulesTable
            data={results?.hits ?? []}
            isLoading={isLoading}
            error={error?.response?.data?.message}
            sortController={sortController}
            setSortController={setSortController}
            handleChangeSort={handleChangeSort}
            actionsSlot={actionsSlot}
          />

          <Divider sx={{ mt: 'auto' }} />

          <Pagination
            disabled={isLoading}
            isFetching={isFetching}
            changePage={handleChangePage}
            rowsCount={results?.estimatedTotalHits || 0}
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            handlePerPageChange={handleChangeLimit}
          />
        </Page.ContentContainer>
      </Page>

      <EditBookingRulesNameDialog
        open={isEditBookingRulesNameDialogOpen}
        onCancel={() => {
          handleCloseEditBookingRulesNameDialog();
          setSelectedBookingRules(null);
        }}
        token={selectedBookingRules?.token ?? ''}
        name={selectedBookingRules?.name ?? ''}
      />

      <DeleteBookingRulesDialog
        open={isDeleteBookingRulesDialogOpen}
        onCancel={() => {
          handleCloseDeleteBookingRulesDialog();
          setSelectedBookingRules(null);
        }}
        token={selectedBookingRules?.token ?? ''}
        name={selectedBookingRules?.name ?? ''}
        employeesGroupsTokens={
          selectedBookingRules?.employeesGroupsTokens ?? []
        }
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'BookingRules'),
  BookingRules,
);
